import { Button, ProviderLayoutJumpNav, ProviderLayoutPageHeader, ProviderLayoutPageTitle } from '@uplift/ui-v2';
import { PreviewProfileModal } from './PreviewProfileModal.component';
import { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { PROFILE_AVATAR_SIZE, ProfileAvatar } from './ProfileAvatar';
import { useProfileContext } from '../profile.context';
import { RequiredText } from './RequiredText.component';
import { useDisplayInvalidRows } from '../profile.utils';

export type ProfilePageHeaderProps = {
  preventPreview?: boolean;
};

export const enum ProfileTabEnum {
  aboutYou = 'aboutYou',
  bio = 'bio',
  yourPractice = 'yourPractice',
  clinicalExperience = 'clinicalExperience',
  crendentialing = 'crendentialing',
}

const sectionIds = [ ProfileTabEnum.aboutYou, ProfileTabEnum.bio, ProfileTabEnum.yourPractice, ProfileTabEnum.clinicalExperience, ProfileTabEnum.crendentialing ];

const profileTabTitles: Record<ProfileTabEnum, string> = {
  [ProfileTabEnum.aboutYou]: 'About You',
  [ProfileTabEnum.bio]: 'Bio',
  [ProfileTabEnum.yourPractice]: 'Your Practice',
  [ProfileTabEnum.clinicalExperience]: 'Clinical Experience',
  [ProfileTabEnum.crendentialing]: 'Credentialing',
};

export const ProfilePageHeader: React.VFC<ProfilePageHeaderProps> = props => {
  const { profile } = useProfileContext();
  const [ preview, setPreview ] = useState(false);
  const displayInvalidPhoto = useDisplayInvalidRows().includes('photo');
  const { classes, cx } = useProfilePageHeaderStyles();

  const title = useMemo(() => {

    return (
      <Box>
        <Box display="flex" alignItems="center" gap="1.5rem" flexDirection={{ xs: 'column', sm: 'row' }} mb={{ xs: 2, sm: 0 }}>
          <Box display="flex" flexDirection="column" alignItems="center">
            <Box position="relative" display="flex" flexDirection="column" alignItems="center">
              <ProfileAvatar />
              {displayInvalidPhoto && (
                <Box className={classes.photoRequiredTooltip}>
                  <RequiredText>
                    Photo is required
                  </RequiredText>
                </Box>
              )}
            </Box>
          </Box>
          <Box>
            <Typography variant="overline" color="textSecondary">Matching Profile</Typography>
            <ProviderLayoutPageTitle title={profile.nameDisplay.fullWithAffixes} />
          </Box>
        </Box>
      </Box>
    );
  }, [ classes.photoRequiredTooltip, displayInvalidPhoto, profile.nameDisplay.fullWithAffixes ]);

  return (
    <>
      <PreviewProfileModal open={preview} onClose={() => setPreview(false)} />
      <ProviderLayoutPageHeader
        classes={{
          content: cx(classes.headerContent, {
            [classes.spacingForInvalidPhotoTooltip]: displayInvalidPhoto
          }),
          actions: classes.headerActions
        }}
        title={title}
        actions={!props.preventPreview && <Button variant="outlined" color="secondary" fullWidth onClick={() => setPreview(true)}>Preview Profile</Button>}
        jumpNav={(
          <ProviderLayoutJumpNav
            tabs={sectionIds.map(sectionId => ({ label: profileTabTitles[sectionId], sectionId }))}
            initialNavValue={ProfileTabEnum.aboutYou}
          />
        )}
      />
    </>
  );
};

const ARROW_SIZE = 14;

const useProfilePageHeaderStyles = makeStyles({ name: 'ProfilePageHeader' })(theme => ({
  /*
    * This gives space for the absolute positioned tooltip to be displayed under the profile avatar
    */
  spacingForInvalidPhotoTooltip: {
    marginBottom: theme.spacing(9),

    [theme.breakpoints.down('sm')]: {
      marginBottom: 0,
    }
  },
  headerContent: {
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      textAlign: 'center',
      alignItems: 'center',
    }
  },
  headerActions: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 'unset',
    }
  },
  photoRequiredTooltip: {
    position: 'relative',
    width: 'fit-content',
    backgroundColor: theme.palette.warning.background,
    padding: theme.spacing(2),

    marginTop: `calc(${ARROW_SIZE}px + ${theme.spacing(2)})`,
    [theme.breakpoints.up('sm')]: {

      marginTop: `${ARROW_SIZE + 2}px`,
      position: 'absolute',
      top: '100%',
      left: 0,
      whiteSpace: 'nowrap',
    },

    '&::before': {
      content: '""',
      position: 'absolute',
      width: 0,
      height: 0,
      borderLeft: `${ARROW_SIZE}px solid transparent`,
      borderRight: `${ARROW_SIZE}px solid transparent`,
      borderBottom: `${ARROW_SIZE}px solid ${theme.palette.warning.background}`,
      top: '-12px',
      left: '50%',
      transform: 'translateX(-50%)',
      [theme.breakpoints.up('sm')]: {
        left: `${PROFILE_AVATAR_SIZE / 2}px`
      }
    }
  }
}));
